import { useQuery } from '@tanstack/react-query';
import GatewayApiClient from '../clients/gateway-api/gateway-api.client';

import { queryClient } from '../query-client';
import { Strategy } from '@oai-frontend-monorepo/types';
import {
  parseUnderlyingSymbol,
  parseUnderlyingSymbolFromPosition,
} from '@oai-frontend-monorepo/utils';
import { useBalance } from './useBalance';

const queryFn = async (brokerageAccountNumber?: string) => {
  const res = await GatewayApiClient.getPositions(brokerageAccountNumber);

  const updatedRes = res.map((p) => {
    if (!p.legPositions?.length) return p;

    const updatedLegPositions = p.legPositions.map((l) => {
      if (!l.symbol || l.symbol.length < 6) return l;

      const underlying = parseUnderlyingSymbol(l.symbol);

      return underlying === 'SQ'
        ? { ...l, symbol: l.symbol.replace('SQ', 'XYZ') }
        : l;
    });

    return { ...p, legPositions: updatedLegPositions };
  });

  updatedRes.forEach((p) => {
    // set individual position in cache
    queryClient.setQueryData(['positions', p.positionId], p);

    return p.positionId;
  });

  return updatedRes.filter(
    (p) =>
      p.strategyName !== Strategy.SHORT_STOCK &&
      parseUnderlyingSymbolFromPosition(p) !== 'NKLA1'
  );
};

export const prefetchPositions = async (brokerageAccountNumber = '') => {
  return await queryClient.prefetchQuery({
    queryKey: ['positions', brokerageAccountNumber],
    queryFn: () => queryFn(brokerageAccountNumber),
  });
};

export const ensurePositions = async (brokerageAccountNumber = '') => {
  return await queryClient.ensureQueryData({
    queryKey: ['positions'],
    queryFn: () => queryFn(brokerageAccountNumber),
    retry: false,
  });
};

export const usePositions = () => {
  const { data } = useBalance();

  const accountNumber = data?.balances?.account_number || '';
  return useQuery({
    queryKey: ['positions', accountNumber],
    queryFn: () => queryFn(accountNumber),
    staleTime: 1000 * 60,
    refetchInterval: 20000,
  });
};
